import { useState } from 'react'
import type { RateHistory } from '../api/types'
import { Amt, Card, Dt, Empty, Field, Note, PageHead, TotalBox, Wt } from '../components/ui'
import { PersonPicker } from '../components/PersonPicker'
import { useApi } from '../hooks/useApi'
import { useItemPicker } from '../hooks/usePickers'
import { addDays, today } from '../lib/format'

/**
 * What a flower was going for, day by day.
 *
 * Each day rate is the money taken divided by the weight sold, not an average
 * of the rates written down. Ten kilos at 180 and a two hundred gram bag at 400
 * do not make the day 290 - the first is what the flower was going for and the
 * second is one small bag to somebody in a hurry.
 */
export function Rates() {
  const items = useItemPicker()
  const [itemId, setItemId] = useState<string | null>(null)
  const [from, setFrom] = useState(addDays(today(), -30))
  const [to, setTo] = useState(today())

  const history = useApi<RateHistory>(
    itemId ? `/reports/rate-history/${itemId}?from=${from}&to=${to}` : null,
    [from, to],
  )

  const data = history.data
  const topRate = data?.points.reduce((max, point) => Math.max(max, Number(point.rate)), 0) ?? 0

  return (
    <>
      <PageHead title="ریٹ کا ریکارڈ" hint="کس دن کیا بھاؤ چل رہا تھا۔" />

      <Card>
        <div className="row">
          <PersonPicker
            label="پھول"
            noun="پھول"
            value={itemId}
            onChange={setItemId}
            people={items.people}
          />

          <Field label="اس تاریخ سے">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>

          <Field label="اس تاریخ تک">
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
      </Card>

      {history.error && <Note kind="error">{history.error}</Note>}
      {!itemId && <Empty>ریٹ دیکھنے کے لیے پھول چنیں۔</Empty>}

      {data && (
        <>
          <div className="totals">
            <TotalBox label="اوسط ریٹ" value={<Amt value={data.overallRate} />} accent />
            <TotalBox label="کل وزن" value={<Wt value={data.totalWeight} />} />
            {data.best && (
              <TotalBox
                label="سب سے اچھا دن"
                value={
                  <span>
                    <Amt value={data.best.rate} /> · <Dt value={data.best.date} />
                  </span>
                }
              />
            )}
            {data.worst && (
              <TotalBox
                label="سب سے کم دن"
                value={
                  <span>
                    <Amt value={data.worst.rate} /> · <Dt value={data.worst.date} />
                  </span>
                }
              />
            )}
          </div>

          <Card title={data.itemName}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>تاریخ</th>
                    <th>وزن</th>
                    <th>رقم</th>
                    <th>ریٹ</th>
                    <th className="bar-cell"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.points.map((point) => (
                    <tr key={point.date}>
                      <td>
                        <Dt value={point.date} />
                      </td>
                      <td className="num">
                        <Wt value={point.weight} />
                      </td>
                      <td className="num">
                        <Amt value={point.amount} tone="muted" />
                      </td>
                      <td className="num">
                        <Amt value={point.rate} />
                      </td>
                      <td className="bar-cell">
                        <div
                          className="bar"
                          style={{
                            width: topRate ? `${(Number(point.rate) / topRate) * 100}%` : '2px',
                          }}
                        />
                      </td>
                    </tr>
                  ))}

                  {data.points.length === 0 && (
                    <tr>
                      <td colSpan={5}>
                        <Empty>اِن دنوں میں اِس پھول کا کوئی سودا نہیں ہوا۔</Empty>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <p className="field__hint">
              ہر دن کا ریٹ وزن کے حساب سے نکالا گیا ہے — یعنی اُس دن کی کل رقم تقسیم کل وزن۔ اِس لیے
              دس کلو کا سودا دو سو گرام کے سودے سے زیادہ وزن رکھتا ہے۔
            </p>
          </Card>
        </>
      )}
    </>
  )
}
